// Sticky banner shown at the top of the operator app when the active org's
// billing subscription is in the `trialing` state. Reads the shared
// useOrgBilling cache so it shares the same fetch with the Settings pages.

import { Link } from 'react-router-dom';
import { Button } from '@pc/ui';
import {
  deriveBillingState,
  isAutoRenewingTrial,
  trialDaysRemaining,
  useOrgBilling,
} from './useOrgBilling';

export function TrialBanner() {
  const billingQ = useOrgBilling();
  const snap = billingQ.data;
  const sub = snap?.subscription ?? null;
  if (!sub) return null;

  // Route through `deriveBillingState` so the platform-admin
  // `dev_paid_override` (migration 20260628100000) suppresses this banner
  // -- the operator is being treated as fully paid even though the row's
  // raw status is still `trialing`. Without this, a QA / internal-demo
  // org with the override on would still see the misleading "FREE TRIAL
  // 363 days left" banner.
  const billing = deriveBillingState(snap, billingQ.isPending || billingQ.isLoading);
  if (billing.isPaid || billing.isDevPaidOverride) return null;
  if (sub.status !== 'trialing') return null;

  // Suppress the banner entirely when the operator is on an
  // auto-renewing trial (has a Stripe subscription that will
  // auto-convert when the trial ends). The "Upgrade now" CTA in this
  // banner routes to /settings/billing, but there is no workflow there
  // to bypass the remaining trial days — the subscription is already
  // committed. Showing the banner in that state is misleading.
  if (isAutoRenewingTrial(snap)) return null;

  const daysLeft = trialDaysRemaining(snap);
  if (daysLeft === null) return null;

  const trialEnd = sub.trial_end ? new Date(sub.trial_end) : null;
  const expired = daysLeft <= 0;

  return (
    <div className="sticky top-0 z-30 border-b border-rule-strong bg-paper-3">
      <div className="px-10 py-2.5 flex items-center justify-between gap-4">
        <div className="text-sm text-ink flex items-baseline gap-3 min-w-0">
          <span className="smallcap shrink-0">Free trial</span>
          <span className="truncate">
            {expired ? (
              <>
                Your trial has ended. Pick a plan to keep access.
              </>
            ) : (
              <>
                <strong className="tabular">{daysLeft}</strong>{' '}
                {daysLeft === 1 ? 'day' : 'days'} left
                {trialEnd ? (
                  <>
                    {' '}
                    &middot; ends{' '}
                    <span className="tabular">{trialEnd.toLocaleDateString()}</span>
                  </>
                ) : null}
                . All modules are unlocked during the trial.
              </>
            )}
          </span>
        </div>
        <Link to="/settings/billing" className="shrink-0">
          <Button size="sm">Upgrade now</Button>
        </Link>
      </div>
    </div>
  );
}
